import { useEffect, useState } from 'react';

/**
 * Custom hook to automatically sync capacity when component mounts
 * @param {boolean} enabled - Whether to enable auto-sync
 * @param {number} delay - Delay in milliseconds before syncing (default: 1000ms)
 */
export const useCapacitySync = (enabled = true, delay = 1000) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  const syncCapacity = async () => {
    if (!enabled) return;
    
    setIsSyncing(true);
    setSyncResult(null);

    try {
      const response = await fetch('/backend/auto_sync_capacity.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (data.success) {
        setSyncResult({
          success: true,
          message: data.message,
          totalUpdated: data.total_updated,
          timestamp: new Date().toISOString()
        });
        setLastSync(new Date().toISOString());
      } else {
        setSyncResult({
          success: false,
          error: data.error,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      setSyncResult({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (enabled) {
      const timeoutId = setTimeout(syncCapacity, delay);
      return () => clearTimeout(timeoutId);
    }
  }, [enabled, delay]);

  return {
    syncCapacity,
    isSyncing,
    syncResult,
    lastSync
  };
};

export default useCapacitySync;
